import { useState } from "react";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Bell, BellRinging, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Alert-rule button + dialog for a single watchlist symbol.
 *
 * If ``alert`` is already set, shows a filled bell with the current threshold.
 * Clicking opens a dialog to edit or clear the rule. Backend fires an in-app
 * notification when the symbol's day change_pct crosses the threshold in the
 * chosen direction (up/down/any) — deduped to 6h cooldown.
 */
export default function WatchlistAlertButton({ symbol, alert, onSaved }) {
  const [open, setOpen] = useState(false);
  const [threshold, setThreshold] = useState(alert?.threshold_pct ?? 5);
  const [direction, setDirection] = useState(alert?.direction ?? "any");
  const active = !!alert?.active;

  const save = async (nextActive = true) => {
    try {
      await api.patch(`/watchlist/${symbol}/alert`, {
        threshold_pct: Number(threshold) || 0,
        direction,
        active: nextActive,
      });
      toast.success(nextActive ? `Alert set for ${symbol}` : `Alert cleared`);
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid={`watchlist-alert-btn-${symbol}`}
          title={active ? `Alert: ${alert.direction} ${alert.threshold_pct}%` : "Set price alert"}
          className={`text-xs inline-flex items-center gap-1 ${
            active ? "text-[#00C805]" : "text-zinc-500 hover:text-white"
          }`}
        >
          {active ? (
            <>
              <BellRinging size={12} weight="fill" />
              <span className="mono text-[10px]">{alert.direction} {alert.threshold_pct}%</span>
            </>
          ) : (
            <>
              <Bell size={12} /> Alert
            </>
          )}
        </button>
      </DialogTrigger>
      <DialogContent
        className="bg-zinc-950 border-zinc-800 max-w-sm text-white"
        data-testid={`watchlist-alert-dialog-${symbol}`}
      >
        <DialogHeader>
          <DialogTitle className="mono">{symbol} price alert</DialogTitle>
          <DialogDescription className="mono text-xs text-zinc-500">
            Ping me when the daily change crosses my threshold. Deduped to 6h.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          <div>
            <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest mb-1">Threshold %</div>
            <Input
              type="number"
              step="0.1"
              min="0.1"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              className="bg-zinc-900 border-zinc-800 mono"
              data-testid={`watchlist-alert-threshold-${symbol}`}
            />
          </div>

          <div>
            <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest mb-1">Direction</div>
            <div className="flex gap-1">
              {[
                { v: "up", label: "Up ▲" },
                { v: "down", label: "Down ▼" },
                { v: "any", label: "Either" },
              ].map((o) => (
                <button
                  key={o.v}
                  onClick={() => setDirection(o.v)}
                  data-testid={`watchlist-alert-dir-${symbol}-${o.v}`}
                  className={`flex-1 mono text-[10px] uppercase tracking-widest py-2 rounded-sm border ${
                    direction === o.v
                      ? "bg-white text-black border-white"
                      : "border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-white"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          {active && (
            <div className="p-2 rounded-sm border border-amber-500/30 bg-amber-500/5 mono text-[10px] text-amber-300 flex items-start gap-1.5">
              <Warning size={11} weight="bold" className="mt-0.5" />
              Alert currently active — saving overwrites it.
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <Button
              onClick={() => save(true)}
              data-testid={`watchlist-alert-save-${symbol}`}
              className="flex-1 bg-white text-black hover:bg-zinc-200 rounded-sm mono text-[10px] uppercase tracking-widest"
            >
              Save
            </Button>
            {active && (
              <Button
                onClick={() => save(false)}
                data-testid={`watchlist-alert-clear-${symbol}`}
                variant="outline"
                className="border-zinc-700 bg-transparent hover:bg-zinc-900 rounded-sm mono text-[10px] uppercase tracking-widest"
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
